import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from 'components/layout';
import invoiceService from 'services/invoiceService';
import './InvoiceDetailView.css';

// Documentos que se pueden visualizar. Para agregar otro (ej. PDF original,
// orden de compra) solo agrega un objeto con el tipo que acepta
// invoiceService.viewInvoiceFileUrl.
const DOCUMENTS = [
    { id: 'firmado', label: 'Factura Firmada', icon: 'bi-file-earmark-check' },
];

export const InvoiceDetailView = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const viewerRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [invoice, setInvoice] = useState(null);
    const [activeDoc, setActiveDoc] = useState(DOCUMENTS[0].id);
    const [showInfo, setShowInfo] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const res = await invoiceService.getInvoiceById(invoiceId);
                if (!res.error) setInvoice(res.data.data);
            } catch (error) {
                console.error('Error al cargar factura:', error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [invoiceId]);

    useEffect(() => {
        if (invoice?.folio) document.title = `Factura ${invoice.folio}`;
    }, [invoice]);

    const formatDate = (s) => {
        if (!s) return '—';
        return new Date(s).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    };
    const formatCurrency = (n) =>
        new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);

    const statusTranslations = {
        recibida: 'Recibida', en_revision: 'En Revisión', aprobada: 'Aprobada',
        rechazada: 'Rechazada', pagada: 'Pagada', incumplida: 'Incumplida',
    };

    const docUrl = invoiceService.viewInvoiceFileUrl(invoiceId, activeDoc);

    const handleFullscreen = () => {
        const el = viewerRef.current;
        if (!el) return;
        if (document.fullscreenElement) document.exitFullscreen();
        else el.requestFullscreen?.();
    };

    // Si la pestaña se abrió desde el modal, la cierra; si no, regresa al dashboard
    const handleClose = () => {
        window.close();
        setTimeout(() => navigate('/welcome'), 150);
    };

    if (loading) {
        return (
            <Layout>
                <div className="container">
                    <div className="invoice-detail-loading">
                        <div className="spinner"></div>
                        <span>Cargando factura...</span>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!invoice) {
        return (
            <Layout>
                <div className="container">
                    <div className="invoice-detail-loading">
                        <i className="bi bi-exclamation-triangle" style={{ fontSize: '3rem', color: 'var(--color-warning)' }}></i>
                        <span>No se pudo cargar la factura</span>
                        <button className="btn btn-secondary" onClick={() => navigate('/welcome')}>
                            Regresar
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const metodoPago = invoice.metodo_pago || invoice.datos_xml?.metodoPago;

    const fields = [
        { icon: 'bi-upc', label: 'UUID', value: invoice.uuid, mono: true, full: true },
        { icon: 'bi-building', label: 'Proveedor', value: invoice.proveedor_nombre || 'N/A', full: true },
        { icon: 'bi-person-badge', label: 'RFC Emisor', value: invoice.rfc_emisor, mono: true },
        { icon: 'bi-person-badge-fill', label: 'RFC Receptor', value: invoice.rfc_receptor, mono: true },
        { icon: 'bi-calendar-event', label: 'Fecha', value: formatDate(invoice.fecha) },
        { icon: 'bi-credit-card', label: 'Método de Pago', value: metodoPago || 'N/A' },
        { icon: 'bi-diagram-3', label: 'Centro de Costo', value: invoice.centro_costo_nombre || 'N/A' },
        { icon: 'bi-tags', label: 'Tipo de Gasto', value: invoice.tipo_gasto_nombre || 'N/A' },
        invoice.fecha_pago_programada && {
            icon: 'bi-calendar-check', label: 'Pago programado', value: formatDate(invoice.fecha_pago_programada), full: true,
        },
    ].filter(Boolean);

    return (
        <Layout>
            <div className="invoice-detail-page">
                {/* ── Encabezado ── */}
                <div className="invoice-detail-header">
                    <div>
                        <div className="invoice-detail-eyebrow">Detalle de factura</div>
                        <h1 className="invoice-detail-title">
                            {invoice.folio}
                            <span className={`status-badge status-${invoice.estatus}`}>
                                {statusTranslations[invoice.estatus] || invoice.estatus}
                            </span>
                        </h1>
                        <div className="invoice-detail-sub">
                            {invoice.proveedor_nombre} · <strong>{formatCurrency(invoice.total)}</strong>
                        </div>
                    </div>
                    <div className="invoice-detail-actions">
                        <button className="btn btn-secondary" onClick={() => setShowInfo(v => !v)}>
                            <i className={`bi ${showInfo ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar'}`}></i>
                            {showInfo ? 'Ocultar datos' : 'Mostrar datos'}
                        </button>
                        <button className="btn btn-secondary" onClick={handleClose}>
                            <i className="bi bi-x-lg"></i> Cerrar
                        </button>
                    </div>
                </div>

                <div className={`invoice-detail-body ${showInfo ? '' : 'info-hidden'}`}>
                    {/* ── Datos ── */}
                    {showInfo && (
                        <aside className="invoice-detail-info">
                            {invoice.notas_rechazo && (
                                <div className="invoice-detail-alert">
                                    <i className="bi bi-exclamation-octagon-fill"></i>
                                    <div>
                                        <strong>Notas de rechazo</strong>
                                        <p>{invoice.notas_rechazo}</p>
                                    </div>
                                </div>
                            )}

                            <div className="invoice-detail-fields">
                                {fields.map(f => (
                                    <div key={f.label} className={`invoice-detail-field ${f.full ? 'full' : ''}`}>
                                        <div className="field-label"><i className={`bi ${f.icon}`}></i> {f.label}</div>
                                        <div className={`field-value ${f.mono ? 'mono' : ''}`}>{f.value}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="invoice-detail-totals">
                                <div><span>Subtotal</span><span>{formatCurrency(invoice.subtotal)}</span></div>
                                <div><span>IVA</span><span>{formatCurrency(invoice.iva)}</span></div>
                                <div className="grand"><span>Total</span><span>{formatCurrency(invoice.total)}</span></div>
                            </div>

                            {invoice.notas && (
                                <div className="invoice-detail-notes">
                                    <div className="field-label"><i className="bi bi-chat-left-text"></i> Notas</div>
                                    <p>{invoice.notas}</p>
                                </div>
                            )}
                        </aside>
                    )}

                    {/* ── Visor de documento ── */}
                    <section className="invoice-detail-viewer" ref={viewerRef}>
                        <div className="viewer-toolbar">
                            <div className="viewer-tabs">
                                {DOCUMENTS.map(d => (
                                    <button
                                        key={d.id}
                                        className={`viewer-tab ${activeDoc === d.id ? 'active' : ''}`}
                                        onClick={() => setActiveDoc(d.id)}
                                    >
                                        <i className={`bi ${d.icon}`}></i> {d.label}
                                    </button>
                                ))}
                            </div>
                            <div className="viewer-tools">
                                <a href={docUrl} target="_blank" rel="noopener noreferrer" className="viewer-tool" title="Abrir solo el documento">
                                    <i className="bi bi-box-arrow-up-right"></i>
                                </a>
                                <button className="viewer-tool" onClick={handleFullscreen} title="Pantalla completa">
                                    <i className="bi bi-arrows-fullscreen"></i>
                                </button>
                            </div>
                        </div>
                        <iframe key={activeDoc} src={docUrl} title="Documento de la factura" className="viewer-frame" />
                    </section>
                </div>
            </div>
        </Layout>
    );
};
